import React, { useCallback, useEffect, useMemo } from 'react'

export interface ItemSelectorInit<ITEM extends { uuid: string }, BASE extends { uuid: string }> {
  createItem(base: BASE): ITEM

  limitToOptions?: boolean
}

export default function useItemSelector<ITEM extends { uuid: string }, BASE extends { uuid: string }>(
  init: ItemSelectorInit<ITEM, BASE>,
  items: ITEM[],
  setItems: React.Dispatch<React.SetStateAction<ITEM[]>>,
  options: BASE[]
) {
  const extractItem = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | BASE | string, selected?: boolean) => {
      if (typeof e === 'string') {
        const base = options.find(({ uuid }) => uuid === e)
        return [e, base && init.createItem(base), selected]
      }

      if ('uuid' in e) {
        const item = init.createItem(e)
        return [item.uuid, item, selected]
      }

      if (e.type === 'change') {
        const { value, checked } = e.currentTarget as any
        const base = options.find(({ uuid }) => uuid === value)
        return [value, base && init.createItem(base!), checked]
      }

      if (e.type === 'click') {
        const uuid = e.currentTarget.dataset.itemId
        const base = options.find((base) => base.uuid === uuid)
        return [uuid, base && init.createItem(base!), true]
      }

      return [undefined, undefined, selected]
    },
    [init.createItem, options]
  )

  const handleSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | BASE | string, selected?: boolean) => {
      const [uuid, item, isSelected] = extractItem(e, selected)
      if (!uuid) {
        return
      }
      if (!isSelected) {
        setItems((prev) => prev.filter((item) => item.uuid !== uuid))
      } else {
        if (!item) {
          console.warn(`Select non-existing item ${uuid}`)
          return
        }
        setItems((prev) => {
          const next = [...prev]
          const idx = next.findIndex((item) => item.uuid === uuid)
          if (idx >= 0) {
            next[idx] = item!
          } else {
            next.push(item!)
          }
          return next
        })
      }
    },
    [setItems, extractItem]
  )

  const handleDeselected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | BASE | string, selected?: boolean) => {
      const [uuid] = extractItem(e, selected)
      if (!uuid) {
        return
      }
      setItems((prev) => prev.filter((item) => item.uuid !== uuid))
    },
    [setItems, extractItem]
  )

  const handleUpdate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | BASE | string, update: Partial<ITEM>) => {
      const [uuid] = extractItem(e, false)
      if (!uuid) {
        console.warn(`Update item without uuid`)
        return
      }
      setItems((prev) => {
        const next = [...prev]
        const idx = next.findIndex((item) => item.uuid === uuid)
        if (idx === -1) {
          console.warn(`Update non-existing item ${uuid}`)
          return prev
        }
        next[idx] = { ...next[idx], ...update }
        return next
      })
    },
    [setItems, extractItem]
  )

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | boolean, selected?: boolean) => {
      if (e === true || e === false) {
        selected = e
      } else if (e.type === 'click') {
        selected = typeof selected === 'boolean' ? selected : true
      } else if (e.type === 'change') {
        selected = typeof selected === 'boolean' ? selected : (e.currentTarget as any).checked
      }

      if (selected) {
        setItems(options.map((base) => init.createItem(base)))
      } else {
        setItems([])
      }
    },
    [init.createItem, setItems, options]
  )

  useEffect(() => {
    if (init.limitToOptions) {
      const optionIds = new Set(options.map(({ uuid }) => uuid))
      setItems((prev) => prev.filter(({ uuid }) => optionIds.has(uuid)))
    }
  }, [init.limitToOptions, options])

  const ids = useMemo(() => Object.fromEntries(items.map(({ uuid }) => [uuid, true])), [items])

  return { items, ids, setItems, handleSelected, handleDeselected, handleUpdate, handleSelectAll }
}
